import '../AdDetail.pcss';
import './CtmAdDetail.pcss';
import { useSelector } from 'react-redux';
import React, { useEffect, useState } from 'react';
import { Label } from 'reactstrap';
import classnames from 'classnames';
import useRouter from 'use-react-router';
import { isEmpty, omit } from 'lodash';
import { CtmAdModel } from '@mm/state/model/CtmAdModel';
import { useCtmAdCRUDActions } from '@mm/state/actions/CtmAdCRUDActions';
import AdConfigModel from '@mm/state/model/AdConfigModel';
import { MessengerAdModel } from '@mm/state/model/MessengerAdModel';
import { useAdConfigFactory } from './use-ad-config-factory';
import FormStepContainer from '../AdConfigDetail/FormStepContainer/FormStepContainer';
import { AdConfigDetail } from '../AdConfigDetail/AdConfigDetail';
import { MessengerAdDetail } from '../MessengerAdDetail/MessengerAdDetail';
import { FacebookAdsAlert } from '../../../common/components/FacebookAdsAlert/FacebookAdsAlert';
import { activeFacebookPagePlanInfoSelector } from '@mm/state/selectors/facebookPageSelector';
import { selectAdAccounts } from '@mm/state/selectors/adAccountsSelecotrs';
import { useSyncedMessengerAdFactory } from '../../../beta/modules/Campaigns/use-synced-messenger-factory';
import { useHistory } from 'react-router-dom';
import { BotBuilderRoutes } from '../../../../../routes/bot-builder-routes';
import CollapsibleContentContainer from '../../../../../common/components/CollapsibleContentContainer/CollapsibleContentContainer';

type RouteParams = { chatbotId: string; adId: string };

const Alert = ({ isFree, ctmAd }: { isFree?: boolean; ctmAd?: CtmAdModel }) => {
  if (!ctmAd) {
    return null;
  }
  const needsAlert = !ctmAd.ad.draft && isFree;
  if (needsAlert) {
    return (
      <FacebookAdsAlert
        className="ad-detail__publish-alert"
        forSponsored={false}
        trialEnded={ctmAd.publishedOnTrial}
      />
    );
  }
  return null;
};

export const FirstStep = (params: {
  chatbotId: number;
  currentStep?: number;
  onNext?: () => void;
  ctmAd: CtmAdModel;
  touched?: boolean;
  currentAd: MessengerAdModel | AdConfigModel | null;
  onUpdated: (updatedAd: CtmAdModel) => void;
  localAdConfig: Partial<AdConfigModel>;
  localMessengerAd: Partial<MessengerAdModel>;
  setLocalAdConfig: (adConfig: Partial<AdConfigModel>) => void;
  setLocalMessengerAd: (messengerAd: Partial<MessengerAdModel>) => void;
  adConfigFactory: () => Partial<AdConfigModel>;
  messengerAdFactory: () => Partial<MessengerAdModel>;
  usedInSlideout?: boolean;
}) => {
  const {
    ctmAd,
    currentStep,
    touched,
    onNext,
    chatbotId,
    onUpdated,
    currentAd,
    localAdConfig,
    localMessengerAd,
    setLocalAdConfig,
    setLocalMessengerAd,
    messengerAdFactory,
    adConfigFactory,
    usedInSlideout,
  } = params;
  const ctmAdCRUDActions = useCtmAdCRUDActions();
  const adAccounts = useSelector(selectAdAccounts);
  const synced = ctmAd.adKind === 'synced_messenger_ad';
  const disabled =
    !ctmAd.ad.draft ||
    (ctmAd.ad as MessengerAdModel).inProgress ||
    (ctmAd.ad as AdConfigModel).publishingProcess;
  const radioLabelClasses = classnames('ad-detail__radio-label', {
    'ad-detail__radio-label--disabled': disabled,
  });
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    if (currentAd !== null) {
      if (synced) {
        setLocalMessengerAd(currentAd as MessengerAdModel);
      } else {
        setLocalAdConfig(currentAd as AdConfigModel);
      }
    }
  }, [currentAd]);

  useEffect(() => {
    if (currentStep && currentStep > 0) {
      setExpanded(false);
    }
  }, [currentStep]);

  useEffect(() => {
    if (synced) {
      setLocalMessengerAd(ctmAd.ad as MessengerAdModel);
    } else {
      setLocalAdConfig(ctmAd.ad as AdConfigModel);
    }
  }, [ctmAd.adKind]);

  const defaultConfig = () => {
    if (ctmAd !== null) {
      if (synced) {
        const adAccountId =
          localMessengerAd?.adAccountId || ctmAd.ad.adAccountId;
        return {
          ...adConfigFactory(),
          adAccountName:
            localMessengerAd?.adAccountName || ctmAd.ad.adAccountName,
          adAccountId: localMessengerAd?.adAccountId || ctmAd.ad.adAccountId,
          differentUser: adAccounts.find(a => a.id === adAccountId)
            ?.differentUser,
        };
      }
      const adAccountId = localAdConfig?.adAccountId || ctmAd.ad.adAccountId;
      return {
        ...messengerAdFactory(),
        adAccountName: localAdConfig?.adAccountName || ctmAd.ad.adAccountName,
        adAccountId: localAdConfig?.adAccountId || ctmAd.ad.adAccountId,
        differentUser: adAccounts.find(a => a.id === adAccountId)
          ?.differentUser,
      };
    }
    return {};
  };

  if (ctmAd !== null) {
    const onChange = async () => {
      const params = synced
        ? {
            ...omit(
              isEmpty(localAdConfig)
                ? defaultConfig()
                : {
                    ...localAdConfig,
                    name:
                      (localMessengerAd.name !== ctmAd.ad.name
                        ? localMessengerAd.name
                        : null) || localAdConfig.name,
                  },
              'id',
            ),
          }
        : {
            ...omit(
              isEmpty(localMessengerAd)
                ? defaultConfig()
                : {
                    ...localMessengerAd,
                    name:
                      (localAdConfig.name !== ctmAd.ad.name
                        ? localAdConfig.name
                        : null) || localMessengerAd.name,
                  },
              'id',
            ),
          };

      const updatedCtmAd = await ctmAdCRUDActions.update(chatbotId, {
        ...params,
        id: ctmAd.id,
        adAccountId: ctmAd.ad.adAccountId || adAccounts[0]?.id,
      });
      onUpdated(updatedCtmAd);
    };

    return !usedInSlideout ? (
      <FormStepContainer
        valid={true}
        index={1}
        touched={touched}
        header="How do you want to set up your messenger ad?"
        contentClassName="ad-detail__form-step-collapsible-content"
        className="ad-detail__form-step"
        collapsed={false}
        nextAllowed={true}
        last={false}
        onNext={() => {
          if (onNext) {
            onNext();
          }
        }}
        showOrderNumber={true}
        stepsStatus={[expanded]}
        setStepsStatus={() => setExpanded(!expanded)}
        hideNextButton={false}
      >
        <div className="ctm-ad-detail__ad-kind-choices">
          <div className="ctm-ad-detail__synced-choice ad-detail__radio-container">
            <input
              type="radio"
              className="ad-detail__radio-input"
              id="use-synced-ad-choice"
              disabled={disabled}
              checked={synced}
              onChange={onChange}
            />
            <Label className={radioLabelClasses} for="use-synced-ad-choice">
              Sync an existing ad created in Ads Manager
            </Label>
          </div>
          <div className="ctm-ad-detail__new-choice ad-detail__radio-container">
            <input
              type="radio"
              className="ad-detail__radio-input"
              id="use-new-ad-choice"
              disabled={disabled}
              checked={!synced}
              onChange={onChange}
            />
            <Label className={radioLabelClasses} for="use-new-ad-choice">
              Create ad directly in Customers.ai
            </Label>
          </div>
        </div>
      </FormStepContainer>
    ) : (
      <CollapsibleContentContainer
        orderNumber="1"
        containerName="How do you want to set up your messenger ad?"
        className={classnames(
          'ad-detail__slideout-step-container',
          'ad-detail__slideout-step-container-first-step',
        )}
        stepperStylingPlatform="facebook"
        useCircleNumberStyle
      >
        <div className="ctm-ad-detail__ad-kind-choices">
          <div className="ctm-ad-detail__synced-choice ad-detail__radio-container">
            <input
              type="radio"
              className="ad-detail__radio-input"
              id="use-synced-ad-choice"
              disabled={disabled}
              checked={synced}
              onChange={onChange}
            />
            <Label className={radioLabelClasses} for="use-synced-ad-choice">
              Sync an existing ad created in Ads Manager
            </Label>
          </div>
          <div className="ctm-ad-detail__new-choice ad-detail__radio-container">
            <input
              type="radio"
              className="ad-detail__radio-input"
              id="use-new-ad-choice"
              disabled={disabled}
              checked={!synced}
              onChange={onChange}
            />
            <Label className={radioLabelClasses} for="use-new-ad-choice">
              Create ad directly in Customers.ai
            </Label>
          </div>
        </div>
      </CollapsibleContentContainer>
    );
  }
  return null;
};
type Props = {
  ctmAdOverride?: CtmAdModel;
  botIdOverride?: number;
  usedInSlideout?: boolean;
  setCtmAdOverrride?: any;
  onActivateSuccess?: () => void;
};

export const CtmAdDetail = (props: Props) => {
  const {
    ctmAdOverride,
    setCtmAdOverrride,
    botIdOverride,
    usedInSlideout,
    onActivateSuccess,
  } = props;
  const {
    match: {
      params: { chatbotId, adId },
    },
  } = useRouter<RouteParams>();
  const botId = botIdOverride ? botIdOverride : Number(chatbotId);
  const adID = ctmAdOverride ? ctmAdOverride.id : adId;
  const activeFacebookPagePlanInfo = useSelector(
    activeFacebookPagePlanInfoSelector,
  ).info;
  const [ctmAd, setCtmAd] = useState<CtmAdModel | null>(null);
  const [active, setActive] = useState(false);
  const ctmAdCRUDActions = useCtmAdCRUDActions();
  const synced = ctmAd === null || ctmAd?.adKind === 'synced_messenger_ad';
  const [fullyInitialized, setFullyInitialized] = useState(false);
  const adConfigFactory = useAdConfigFactory({ botId });
  const messengerAdFactory = useSyncedMessengerAdFactory({ botId });
  const [localAdConfig, setLocalAdConfig] = useState<Partial<AdConfigModel>>(
    {},
  );
  const [localMessengerAd, setLocalMessengerAd] = useState<
    Partial<MessengerAdModel>
  >({});
  const history = useHistory();

  useEffect(() => {
    (async () => {
      const ad = await ctmAdCRUDActions.retrieve(botId, Number(adID));
      if (!ad && !usedInSlideout) {
        history.push(
          BotBuilderRoutes.messengerAds.getLink({ chatbotId: botId }),
        );
      }
      if (ad && ad.ad.progressStep !== null) {
        setActive(true);
      }
      setCtmAd(ad);
      if (setCtmAdOverrride) {
        setCtmAdOverrride(ad);
      }
      setFullyInitialized(true);
    })();
  }, []);

  const renderFirstStep = ({
    startOver,
    currentStep,
    currentAd,
  }: {
    startOver: () => void;
    currentStep: number;
    currentAd: MessengerAdModel | AdConfigModel | null;
  }) =>
    ctmAd === null ? null : (
      <>
        <Alert
          ctmAd={ctmAd}
          isFree={activeFacebookPagePlanInfo.isFreeOrSpecialPlan}
        />
        <FirstStep
          adConfigFactory={adConfigFactory}
          messengerAdFactory={messengerAdFactory}
          chatbotId={botId}
          localAdConfig={localAdConfig}
          localMessengerAd={localMessengerAd}
          setLocalAdConfig={setLocalAdConfig}
          setLocalMessengerAd={setLocalMessengerAd}
          onUpdated={updatedCtmAd => {
            setCtmAd(updatedCtmAd);
            if (setCtmAdOverrride) {
              setCtmAdOverrride(updatedCtmAd);
            }
          }}
          onNext={() => {
            setActive(true);
            startOver();
          }}
          currentAd={currentAd}
          touched={active}
          currentStep={currentStep}
          ctmAd={ctmAd}
          usedInSlideout={usedInSlideout}
        />
      </>
    );

  if (ctmAd !== null && fullyInitialized && ctmAd?.ad) {
    if (synced) {
      return (
        <MessengerAdDetail
          messengerAdEntity={ctmAd.ad as MessengerAdModel}
          active={active}
          indexOffset={1}
          usedInSlideout={usedInSlideout}
          onActivateSuccess={onActivateSuccess}
        >
          {renderFirstStep}
        </MessengerAdDetail>
      );
    }
    return (
      <AdConfigDetail
        adConfigEntity={ctmAd.ad as AdConfigModel}
        active={active}
        indexOffset={1}
        usedInSlideout={usedInSlideout}
        onActivateSuccess={onActivateSuccess}
      >
        {renderFirstStep}
      </AdConfigDetail>
    );
  }

  return null;
};
